package com.example.socialmediaapp.ui.screens

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import coil3.compose.AsyncImage
import com.example.socialmediaapp.data.models.BusinessOffering
import com.example.socialmediaapp.data.models.BusinessProfile
import com.example.socialmediaapp.data.models.TeamMember
import com.example.socialmediaapp.ui.components.TagRow

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun BusinessProfileScreen(
    profile: BusinessProfile,
    onBack: () -> Unit,
    onFollow: () -> Unit = {},
    onMessage: () -> Unit = {}
) {
    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text(profile.businessName) },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(Icons.Default.ArrowBack, "Back")
                    }
                },
                actions = {
                    IconButton(onClick = { }) {
                        Icon(Icons.Default.Share, "Share")
                    }
                }
            )
        }
    ) { padding ->
        LazyColumn(
            modifier = Modifier.fillMaxSize().padding(padding)
        ) {
            // Banner + Logo
            item { BusinessBanner(profile) }

            // Name, rating, location
            item { BusinessHeader(profile) }

            // Action buttons
            item { BusinessActions(profile, onFollow, onMessage) }

            // Tags
            if (profile.tags.isNotEmpty()) {
                item { TagRow(profile.tags) }
            }

            // About
            if (profile.description != null) {
                item { BusinessAbout(profile.description) }
            }

            // Products & Services
            if (profile.offerings.isNotEmpty()) {
                item {
                    Text(
                        "Products & Services",
                        style = MaterialTheme.typography.titleLarge,
                        fontWeight = FontWeight.Bold,
                        modifier = Modifier.padding(16.dp)
                    )
                }
                items(profile.offerings) { OfferingCard(it) }
            }

            // Team
            if (profile.team.isNotEmpty()) {
                item {
                    Text(
                        "Team",
                        style = MaterialTheme.typography.titleLarge,
                        fontWeight = FontWeight.Bold,
                        modifier = Modifier.padding(16.dp)
                    )
                }
                items(profile.team) { TeamMemberItem(it) }
            }

            // Contact / Social
            item { ContactInfo(profile) }
        }
    }
}

@Composable
private fun BusinessBanner(profile: BusinessProfile) {
    Box(Modifier.fillMaxWidth().height(200.dp)) {
        if (profile.bannerUrl != null) {
            AsyncImage(
                model = profile.bannerUrl,
                contentDescription = null,
                modifier = Modifier.fillMaxSize(),
                contentScale = ContentScale.Crop
            )
        } else {
            Surface(
                modifier = Modifier.fillMaxSize(),
                color = MaterialTheme.colorScheme.primaryContainer
            ) {}
        }

        if (profile.logoUrl != null) {
            Surface(
                modifier = Modifier
                    .align(Alignment.BottomStart)
                    .padding(16.dp)
                    .size(80.dp),
                shape = RoundedCornerShape(12.dp),
                tonalElevation = 4.dp
            ) {
                AsyncImage(
                    model = profile.logoUrl,
                    contentDescription = "Logo",
                    modifier = Modifier.fillMaxSize().padding(8.dp)
                )
            }
        }

        if (profile.isVerified) {
            val badgeColor = when (profile.verificationBadgeType) {
                "premium" -> Color(0xFFFFD700)
                "partner" -> Color(0xFF9C27B0)
                else -> Color(0xFF2196F3)
            }
            Surface(
                modifier = Modifier.align(Alignment.TopEnd).padding(16.dp),
                shape = RoundedCornerShape(20.dp),
                color = badgeColor
            ) {
                Row(
                    modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Icon(Icons.Default.Verified, null, Modifier.size(16.dp), tint = Color.White)
                    Spacer(Modifier.width(4.dp))
                    Text(
                        "Verified",
                        color = Color.White,
                        style = MaterialTheme.typography.labelSmall,
                        fontWeight = FontWeight.Bold
                    )
                }
            }
        }
    }
}

@Composable
private fun BusinessHeader(profile: BusinessProfile) {
    Column(Modifier.fillMaxWidth().padding(16.dp)) {
        Text(
            profile.businessName,
            style = MaterialTheme.typography.headlineMedium,
            fontWeight = FontWeight.Bold
        )
        if (profile.tagline != null) {
            Text(
                profile.tagline,
                style = MaterialTheme.typography.bodyLarge,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier.padding(top = 4.dp)
            )
        }
        Spacer(Modifier.height(12.dp))
        Row(
            horizontalArrangement = Arrangement.spacedBy(16.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (profile.avgRating != null && profile.avgRating > 0) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Default.Star, null, Modifier.size(20.dp), tint = Color(0xFFFFC107))
                    Spacer(Modifier.width(4.dp))
                    Text(
                        run { val r = profile.avgRating; "${r.toInt()}.${((r - r.toInt()) * 10).toInt()}" },
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold
                    )
                    Text(
                        " (${profile.reviewCount})",
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            }
            Text("${profile.followerCount} followers", style = MaterialTheme.typography.bodyMedium)
            if (profile.headquartersCity != null) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Default.LocationOn, null, Modifier.size(16.dp))
                    Text(
                        "${profile.headquartersCity}, ${profile.headquartersCountry ?: ""}",
                        style = MaterialTheme.typography.bodyMedium
                    )
                }
            }
        }
    }
}

@Composable
private fun BusinessActions(
    profile: BusinessProfile,
    onFollow: () -> Unit,
    onMessage: () -> Unit
) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 8.dp),
        horizontalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        Button(onClick = onFollow, modifier = Modifier.weight(1f)) {
            Icon(Icons.Default.Add, null, Modifier.size(18.dp))
            Spacer(Modifier.width(8.dp))
            Text("Follow")
        }
        OutlinedButton(onClick = onMessage, modifier = Modifier.weight(1f)) {
            Icon(Icons.Default.Chat, null, Modifier.size(18.dp))
            Spacer(Modifier.width(8.dp))
            Text("Message")
        }
        if (profile.websiteUrl != null) {
            IconButton(onClick = { }) {
                Icon(Icons.Default.Language, "Website")
            }
        }
    }
}

@Composable
private fun BusinessAbout(description: String) {
    Column(Modifier.fillMaxWidth().padding(16.dp)) {
        Text(
            "About",
            style = MaterialTheme.typography.titleMedium,
            fontWeight = FontWeight.Bold
        )
        Spacer(Modifier.height(8.dp))
        Text(description, style = MaterialTheme.typography.bodyMedium)
    }
}

@Composable
private fun OfferingCard(offering: BusinessOffering) {
    Card(
        modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 4.dp)
    ) {
        Row(modifier = Modifier.padding(16.dp), verticalAlignment = Alignment.CenterVertically) {
            if (offering.imageUrl != null) {
                AsyncImage(
                    model = offering.imageUrl,
                    contentDescription = null,
                    modifier = Modifier.size(60.dp),
                    contentScale = ContentScale.Crop
                )
                Spacer(Modifier.width(12.dp))
            }
            Column(modifier = Modifier.weight(1f)) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Text(offering.name, style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold)
                    if (offering.isFeatured) {
                        Spacer(Modifier.width(8.dp))
                        Surface(
                            shape = RoundedCornerShape(4.dp),
                            color = MaterialTheme.colorScheme.primaryContainer
                        ) {
                            Text(
                                "Featured",
                                modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp),
                                style = MaterialTheme.typography.labelSmall,
                                color = MaterialTheme.colorScheme.onPrimaryContainer
                            )
                        }
                    }
                }
                if (offering.description != null) {
                    Text(
                        offering.description,
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        maxLines = 2
                    )
                }
                if (offering.priceMin != null) {
                    val priceText = if (offering.priceMax != null && offering.priceMax != offering.priceMin) {
                        "$${offering.priceMin} – $${offering.priceMax} ${offering.currency}"
                    } else {
                        "$${offering.priceMin} ${offering.currency}"
                    }
                    Text(
                        priceText,
                        style = MaterialTheme.typography.labelMedium,
                        color = MaterialTheme.colorScheme.primary,
                        fontWeight = FontWeight.SemiBold
                    )
                }
            }
        }
    }
}

@Composable
private fun TeamMemberItem(member: TeamMember) {
    ListItem(
        headlineContent = { Text(member.username, fontWeight = FontWeight.Medium) },
        supportingContent = {
            Text(
                listOfNotNull(member.jobTitle, member.department).joinToString(" · ")
                    .ifEmpty { member.role }
            )
        },
        leadingContent = {
            if (member.avatarUrl != null) {
                AsyncImage(
                    model = member.avatarUrl,
                    contentDescription = null,
                    modifier = Modifier.size(40.dp)
                )
            } else {
                Icon(Icons.Default.Person, null, modifier = Modifier.size(40.dp))
            }
        }
    )
}

@Composable
private fun ContactInfo(profile: BusinessProfile) {
    if (profile.businessEmail != null || profile.businessPhone != null ||
        profile.linkedinUrl != null || profile.twitterUrl != null || profile.instagramUrl != null
    ) {
        Column(Modifier.fillMaxWidth().padding(16.dp)) {
            Text("Contact", style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
            Spacer(Modifier.height(8.dp))
            if (profile.businessEmail != null) {
                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.padding(vertical = 4.dp)) {
                    Icon(Icons.Default.Email, null, Modifier.size(18.dp))
                    Spacer(Modifier.width(8.dp))
                    Text(profile.businessEmail, style = MaterialTheme.typography.bodyMedium)
                }
            }
            if (profile.businessPhone != null) {
                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.padding(vertical = 4.dp)) {
                    Icon(Icons.Default.Phone, null, Modifier.size(18.dp))
                    Spacer(Modifier.width(8.dp))
                    Text(profile.businessPhone, style = MaterialTheme.typography.bodyMedium)
                }
            }
        }
    }
}
